import React, { useState, useEffect } from "react";
import { Flex, Text, DropdownButton, Link, Card } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Sizes } from "@jsq/ds/styles";
import { useGetSamples } from "../../hooks/useGetSamples";
import { useAnalyticsAI } from "../AnalyticsAI/context";
import { ChatInput } from "../ChatInput";
import AITag from "./AITag";
import * as styles from "./styles_vanilla.css";
import type { Explore } from "../../hooks/useGetSamples";

export const PromptView: React.FC = () => {
  const { submitPrompt, submitSample } = useAnalyticsAI();
  const { explores, samplesCollection, defaultExplore } = useGetSamples();
  const [selectedExplore, setSelectedExplore] = useState<Explore | null>(defaultExplore);

  useEffect(() => {
    setSelectedExplore((prev) => prev || defaultExplore);
  }, [defaultExplore]);

  return (
    <Flex flexDirection="column" className={styles.container}>
      <Flex flexDirection="column" gap={Sizes.SM} className={styles.header}>
        <Flex alignItems="center" gap={Sizes.SM}>
          <Text variant="h1">Analytics</Text>
          <AITag scaleMultiplier={1.3} className={styles.aiTag} />
        </Flex>
        <Text variant="secondary-xxl">Ask questions about your data</Text>
        <Flex>
          <Text variant="secondary-md">
            Note: As an early-stage technology, Insights AI can generate output that seems
            plausible but is incorrect. We recommend that you validate all generated fields from
            Insights AI before you rely on it.
            <Link to="#" isInline variant="muted" className={styles.disclaimerLink}>
              Learn more
            </Link>
          </Text>
        </Flex>
      </Flex>
      <Flex alignItems="center" gap={Sizes.SM} className={styles.contextSection}>
        <Text>Let&apos;s ask about</Text>
        <DropdownButton
          label={selectedExplore?.displayName ?? "Set context"}
          data-testid="context-dropdown-button"
        >
          {explores.map((explore) => {
            return (
              <DropdownButton.Action
                key={explore.exploreKey}
                onSelect={() => setSelectedExplore(explore)}
              >
                {explore.displayName}
              </DropdownButton.Action>
            );
          })}
        </DropdownButton>
      </Flex>
      <ChatInput placeholder="What do you want to know?" onSubmit={submitPrompt} />
      <Flex flexDirection="column" gap={Sizes.MD} className={styles.samplePromptsSection}>
        <Text>Get started with a few sample prompts</Text>
        <Flex flexDirection="column" gap={Sizes.SM}>
          {selectedExplore &&
            samplesCollection[selectedExplore.exploreKey]?.map((sample) => {
              const [modelName, exploreId] = sample.key.split(":");
              return (
                <Card
                  key={`${sample.category}-${sample.prompt}`}
                  className={styles.samplePromptCard}
                  onClick={() =>
                    submitSample(sample.prompt, exploreId ?? null, modelName ?? null)
                  }
                >
                  <Text variant="regular-md">{sample.prompt}</Text>
                  <Icon type="arrow-right" />
                </Card>
              );
            })}
        </Flex>
      </Flex>
    </Flex>
  );
};
